<template>
  <div class="v-table-header-sortable-ui">
    <VTableHeader v-slot="{ title }" :titles="titles" :class="variationClassName">
      <button
        v-if="title.sortable"
        type="button"
        class="v-table-header-sortable-ui__btn"
        :aria-label="getAriaLabel(title)"
        @click="onToggleSort(title)"
      >
        {{ title.label }}
        <VTableHeaderSortableArrows :direction="sortingState?.[title.key]" />
      </button>
      <template v-else>
        {{ title.label }}
      </template>
    </VTableHeader>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import {
    type TSortableHeaderItem,
    type TSortingState,
  } from '~/misc/v-table-header-sortable/types';
  import { ESortingDirection } from '~/misc/resource/types';

  // Components
  import VTableHeader from '~/misc/v-table-header/VTableHeader.vue';
  import VTableHeaderSortableArrows from '~/misc/v-table-header-sortable/arrows/VTableHeaderSortableArrows.vue';

  defineProps({
    titles: {
      type: Array as PropType<TSortableHeaderItem[]>,
      default: () => [],
    },
    variationClassName: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits<{
    (e: 'sort', payload: TSortingState | null): void;
  }>();

  const sortingState: Ref<TSortingState | null> = ref<TSortingState | null>(null);

  const setSortingState = (state: TSortingState | null) => {
    sortingState.value = state;
  };

  const getAriaLabel = (title: TSortableHeaderItem): string => {
    const direction: ESortingDirection | undefined = sortingState.value?.[title.key];

    if (direction === ESortingDirection.ASC) return `${title.label}: sorted ascending`;
    if (direction === ESortingDirection.DESC) return `${title.label}: sorted descending`;

    return `${title.label}: not sorted`;
  };

  const getDirectionFor = (
    currentDirection: ESortingDirection | undefined
  ): ESortingDirection | undefined => {
    if (!currentDirection) return ESortingDirection.ASC;
    if (currentDirection === ESortingDirection.ASC) return ESortingDirection.DESC;

    return undefined;
  };

  const emitSort = () => emit('sort', sortingState.value);

  const onToggleSort = (title: TSortableHeaderItem) => {
    const currentDirection: ESortingDirection | undefined = sortingState.value?.[title.key];
    const newDirection: ESortingDirection | undefined = getDirectionFor(currentDirection);

    if (newDirection) {
      setSortingState({ [title.key]: newDirection });
    } else {
      setSortingState(null);
    }

    emitSort();
  };
</script>
